import { FC, useEffect, useState } from "react";
import RecentSearchCard from "../components/RecentSearchCard";
import { Search } from "@/models/search/Search";
import useSearch from "@/hooks/useSearch";
import { RecentSearchesWidgetConfig } from "@/models/dashboard/WidgetConfig";
import { Skeleton } from "@capacit-com/lara-law-shared-ui";
import { Clock } from "lucide-react";
import { dedupeSupersededTagSearches } from "../lib/dedupeSupersededTagSearches";

interface RecentSearchesWidgetProps {
  widgetId: string;
  name: string;
  config: RecentSearchesWidgetConfig;
}

const RecentSearchesWidget: FC<RecentSearchesWidgetProps> = ({
  widgetId: _widgetId,
  name: _name,
  config,
}) => {
  const {
    useMyRecentSearches,
    favoriteSearch,
    unfavoriteSearch,
    deleteSearch,
    isAnyFavoritePending,
    isDeletePending,
    transformStoredSearch,
  } = useSearch();
  const fetchedRecentSearches = useMyRecentSearches();
  const [recentSearches, setRecentSearches] = useState<Search[]>([]);

  useEffect(() => {
    if (fetchedRecentSearches.data && fetchedRecentSearches.isFetched) {
      setRecentSearches(
        dedupeSupersededTagSearches(
          fetchedRecentSearches.data.map(transformStoredSearch)
        )
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchedRecentSearches.data, fetchedRecentSearches.isFetched]);

  // Loading state
  if (fetchedRecentSearches.isPending) {
    return (
      <div className="grid grid-cols-2 gap-2 p-2">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>
    );
  }

  const visibleSearches =
    config.maxItems && config.maxItems > 0
      ? recentSearches.slice(0, config.maxItems)
      : recentSearches;

  if (visibleSearches.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-8 space-y-3 text-muted-foreground">
        <Clock className="h-8 w-8" />
        <p className="text-sm">Ingen seneste søgninger</p>
      </div>
    );
  }

  return (
    <div className="themed-scrollbar h-full overflow-auto">
      <div className="grid grid-cols-2 gap-2 p-2">
        {visibleSearches.map((search) => (
          <div key={search.id} className="min-w-0">
          <RecentSearchCard
            search={search}
            colors={[]} // Empty colors array - uses default Tailwind classes
            favoriteSearch={favoriteSearch.mutateAsync}
            unfavoriteSearch={unfavoriteSearch.mutateAsync}
            deleteSearch={deleteSearch.mutateAsync}
            isDeletingPending={isDeletePending}
            isAnyFavoritePending={isAnyFavoritePending}
          />
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentSearchesWidget;
